import { BillingTierDB } from "../admin/types";

/**
 * Defines Projects State interface
 */
export interface GNProjectMonthToDateStats {
    sessionHours: number,
    sessionCount: number,
    symbolicUploadCount: number,
    symbolicUploadGb: number,
    crashReporterUploadCount: number,
    crashReporterUploadGb: number
}

export interface NotificationsPermissions {
    dailyStats: boolean,
    ensures: boolean,
    crashes: boolean
}
export interface NotificationsPermissionsGroup {
    client: NotificationsPermissions,
    server: NotificationsPermissions,
    editor: NotificationsPermissions
}

export interface SlackIntegration {
    team: string;
    channel: string;
    configurationURL: string;
    permissions: NotificationsPermissionsGroup
}

export interface DiscordIntegration {
    webHookURL: string;
    permissions: NotificationsPermissionsGroup
}

export interface GNProjectPublic {
    projectID: string,
    projectSecret: string,
    projectName: string,
    projectSharedWith: string[]
    projectOwner: string,
    sinceTimestamp: number,
    lastActivityTimestamp: number,
    lastSymbolicFileUploadTimestamp?: number,
    ueCrashReporterIntegration?: boolean,
    notifications: NotificationsPermissionsGroup,
    slackIntegration: SlackIntegration | undefined,
    discordIntegration: DiscordIntegration | undefined,
    billingTierData: BillingTierDB | undefined,
    ueCrashReporterUrl?: string,
    uePluginVersion: string | undefined,
    wsApiID?: string,
    wsApiEndpoint?: string,
    blockTimestamp?: number,
    blockReason?: string,
    monthToDateUsageCost?: number
    monthToDateUsageStats?: GNProjectMonthToDateStats
}
  
  export interface ProjectsState {
      // http state
      httpInProgress: boolean;
      httpUpdateInProgress: boolean;
      httpError: string;
      gnProjects: GNProjectPublic[];
    }
    
    /**
     * Defines Project actions names
     */
    export const PROJECTS_IN_PROGRESS = 'PROJECTS_IN_PROGRESS'
    export const PROJECTS_UPDATE_IN_PROGRESS = 'PROJECTS_UPDATE_IN_PROGRESS'
    export const PROJECTS_ERROR = 'PROJECTS_ERROR'
    export const PROJECTS_RESET_DATA = 'PROJECTS_RESET_DATA'
    export const PROJECTS_GET = 'PROJECTS_GET'
    export const PROJECTS_ADD = 'PROJECTS_ADD'
    export const PROJECTS_DELETE = 'PROJECTS_DELETE'
    export const PROJECTS_UPDATE_PROJECT_DATA = 'PROJECTS_UPDATE_PROJECT_DATA'
    
    /**
     * Defines Projects actions interfaces
     */
    interface ProjectsInProgressAction {
        type: typeof PROJECTS_IN_PROGRESS;
    }
    interface ProjectsUpdateInProgressAction {
        type: typeof PROJECTS_UPDATE_IN_PROGRESS;
    }
    interface ProjectsErrorAction {
        type: typeof PROJECTS_ERROR;
        error: string;
    }
    interface ProjectsResetDataAction {
        type: typeof PROJECTS_RESET_DATA;
    }
    interface ProjectsGetAction {
        type: typeof PROJECTS_GET;
        payload: GNProjectPublic[]
    }
    interface ProjectsAddAction {
        type: typeof PROJECTS_ADD;
        payload: GNProjectPublic[]
    }
    interface ProjectsDeleteAction {
        type: typeof PROJECTS_DELETE;
        payload: GNProjectPublic[]
    }
    interface ProjectsUpdateProjectDataAction {
        type: typeof PROJECTS_UPDATE_PROJECT_DATA;
        payload: GNProjectPublic[]
    }
    
    /**
     * Defines all suported actions for Projects
     */
    export type ProjectsActionTypes =
        ProjectsInProgressAction |
        ProjectsUpdateInProgressAction |
        ProjectsErrorAction |
        ProjectsResetDataAction |
        ProjectsGetAction |
        ProjectsAddAction |
        ProjectsDeleteAction |
        ProjectsUpdateProjectDataAction;
    
    /**
     * Defines ProjectsActionsInterface
     */
    export interface ProjectsActionsInterface {
        projectsGet: () => Promise<GNProjectPublic[]>;
        projectsAdd: (projectName: string, projectBillingTierID: string, projectSharedWith: string[]) => void;
        projectsDelete: (projectID: string) => void;
        projectsResetData: () => void;
        projectsUpdateData: (projectID: string, projectSharedWith: string[]) => void;
        projectsUpdateProjectData: (gnProject: GNProjectPublic) => void
        projectsUpdateBillingTier: (projectID: string, billingTierID: string) => void;
    }